/**
 * Hero — Media: background video and rotating images.
 *
 * render.php ships one still image (the LCP element) and nothing that moves:
 * further slides sit in an inert <template>, the clip's sources in data-src.
 * After the load event this script brings them in, so they never compete with
 * the first paint:
 *
 * - Reduced motion: the moving layer and its toggle are removed; the still
 *   stays. Also when the setting is switched on after load.
 * - Video: only from 768px wide and without Save-Data; otherwise the still
 *   stays. It fades in once frames are playing.
 * - Slideshow: crossfades every data-hero-media-interval seconds, skipping
 *   slides whose image has not decoded yet. It pauses while the tab is hidden
 *   or focus is inside the hero, and on the toggle.
 */

const reducedMotion = window.matchMedia( '(prefers-reduced-motion: reduce)' );
const wideEnoughForVideo = window.matchMedia( '(min-width: 768px)' );

function setToggleState( toggle, paused ) {
	toggle.setAttribute( 'aria-pressed', paused ? 'true' : 'false' );
	toggle.setAttribute(
		'aria-label',
		paused ? toggle.dataset.labelPlay : toggle.dataset.labelPause
	);
}

function stopMotion( hero ) {
	const video = hero.querySelector( '.hero-media__video' );
	if ( video ) {
		video.pause();
		video.remove();
	}
	hero.querySelector( '.hero-media__slides' )?.remove();
	hero.querySelectorAll( '.hero-media__slide:not(.hero-media__still)' ).forEach( ( slide ) => slide.remove() );
	hero.querySelector( '.hero-media__still' )?.classList.add( 'is-active' );
	hero.querySelector( '[data-hero-media-toggle]' )?.remove();
	hero.heroMediaStop?.();
}

function initVideo( hero, toggle ) {
	const video = hero.querySelector( '.hero-media__video' );

	if ( ! video ) {
		return;
	}

	const saveData = navigator.connection?.saveData === true;

	if ( ! wideEnoughForVideo.matches || saveData ) {
		stopMotion( hero );
		return;
	}

	video.querySelectorAll( 'source[data-src]' ).forEach( ( source ) => {
		source.src = source.dataset.src;
		source.removeAttribute( 'data-src' );
	} );
	video.addEventListener( 'playing', () => video.classList.add( 'is-playing' ), { once: true } );
	video.load();

	video.play().catch( () => {
		// Playback was refused (power saving): announce it as paused.
		if ( toggle ) {
			setToggleState( toggle, true );
		}
	} );

	if ( ! toggle ) {
		return;
	}

	toggle.addEventListener( 'click', () => {
		if ( video.paused ) {
			video
				.play()
				.then( () => setToggleState( toggle, false ) )
				.catch( () => setToggleState( toggle, true ) );
		} else {
			video.pause();
			setToggleState( toggle, true );
		}
	} );

	// Keep the control honest if playback is blocked by the browser.
	video.addEventListener( 'pause', () => setToggleState( toggle, true ) );
	video.addEventListener( 'play', () => setToggleState( toggle, false ) );
}

function initSlideshow( hero, toggle ) {
	const template = hero.querySelector( '.hero-media__slides' );
	const first = hero.querySelector( '.hero-media__still' );

	if ( ! template || ! first ) {
		return;
	}

	const overlay = hero.querySelector( '.hero-media__overlay' );
	const added = Array.from( template.content.children );
	overlay.before( ...added );
	template.remove();

	const slides = [ first, ...added ];
	const ready = new Set( [ first ] );
	added.forEach( ( slide ) => {
		slide
			.decode()
			.then( () => ready.add( slide ) )
			.catch( () => slide.remove() );
	} );

	const interval = Math.max( 4, Number( hero.dataset.heroMediaInterval ) || 6 ) * 1000;
	let current = 0;
	let timer = null;
	let pausedByUser = false;
	let focusInside = false;

	const advance = () => {
		const next = slides.findIndex(
			( slide, index ) => index > current && ready.has( slide ) && slide.isConnected
		);
		const target = next === -1 ? 0 : next;
		if ( target === current ) {
			return;
		}
		slides.forEach( ( slide ) => slide.classList.remove( 'is-leaving' ) );
		slides[ current ].classList.replace( 'is-active', 'is-leaving' );
		slides[ target ].classList.add( 'is-active' );
		current = target;
	};

	const sync = () => {
		const run = ! pausedByUser && ! focusInside && ! document.hidden;
		if ( run && ! timer ) {
			timer = window.setInterval( advance, interval );
		} else if ( ! run && timer ) {
			window.clearInterval( timer );
			timer = null;
		}
	};

	hero.heroMediaStop = () => {
		window.clearInterval( timer );
		timer = null;
		slides.forEach( ( slide ) => slide.classList.remove( 'is-leaving' ) );
	};

	hero.addEventListener( 'focusin', () => {
		focusInside = true;
		sync();
	} );
	hero.addEventListener( 'focusout', ( event ) => {
		focusInside = hero.contains( event.relatedTarget );
		sync();
	} );
	document.addEventListener( 'visibilitychange', sync );

	toggle?.addEventListener( 'click', () => {
		pausedByUser = ! pausedByUser;
		setToggleState( toggle, pausedByUser );
		sync();
	} );

	sync();
}

function initHero( hero ) {
	if ( reducedMotion.matches ) {
		stopMotion( hero );
		return;
	}

	const toggle = hero.querySelector( '[data-hero-media-toggle]' );

	if ( hero.dataset.heroMedia === 'video' ) {
		initVideo( hero, toggle );
	} else {
		initSlideshow( hero, toggle );
	}
}

export default function initHeroMedia() {
	const heroes = document.querySelectorAll(
		'[data-hero-media="video"], [data-hero-media="slideshow"]'
	);

	if ( ! heroes.length ) {
		return;
	}

	heroes.forEach( initHero );

	// Reduced motion switched on after load: stop and drop every moving layer, the still stays.
	reducedMotion.addEventListener( 'change', ( event ) => {
		if ( event.matches ) {
			heroes.forEach( stopMotion );
		}
	} );
}

// After load, so nothing that moves is fetched before the still has painted.
if ( document.readyState === 'complete' ) {
	initHeroMedia();
} else {
	window.addEventListener( 'load', initHeroMedia, { once: true } );
}
